import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2, Save, Search, Loader2 } from 'lucide-react';
import AppShell from '../components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

export default function CompanyProfile() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isNo = (i18n.language || 'nb').startsWith('nb');

  const [org, setOrg] = useState(null);
  const [name, setName] = useState('');
  const [orgnr, setOrgnr] = useState('');
  const [naceCode, setNaceCode] = useState('');
  const [naceDescription, setNaceDescription] = useState('');
  const [employees, setEmployees] = useState('');
  const [industry, setIndustry] = useState('');
  const [size, setSize] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lookingUp, setLookingUp] = useState(false);

  const lookupOrgnr = async () => {
    const cleaned = orgnr.replace(/\s+/g, '');
    if (!/^\d{9}$/.test(cleaned)) {
      toast.error(isNo ? 'Skriv inn 9 siffer først' : 'Enter 9 digits first');
      return;
    }
    setLookingUp(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const res = await axios.get(`${API}/ops/brreg/lookup/${cleaned}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data || {};
      if (d.name) setName(d.name);
      if (d.industry_code) setNaceCode(d.industry_code);
      if (d.industry_description) setNaceDescription(d.industry_description);
      if (typeof d.employees === 'number') setEmployees(String(d.employees));
      // Auto-suggest industry from NACE code (only if not already set)
      if (d.suggested_industry && !industry) setIndustry(d.suggested_industry);
      if (d.is_deleted) {
        toast.warning(isNo
          ? 'Bedriften er registrert som slettet i Enhetsregisteret'
          : 'Company is marked as deleted in the registry');
      } else {
        toast.success(isNo
          ? `Fant «${d.name}»${d.address?.city ? ' i ' + d.address.city : ''}`
          : `Found "${d.name}"`);
      }
    } catch (err) {
      const code = err.response?.status;
      if (code === 404) {
        toast.error(isNo ? 'Org.nr. finnes ikke i Enhetsregisteret' : 'Org number not found');
      } else if (code === 400) {
        toast.error(isNo ? 'Ugyldig organisasjonsnummer' : 'Invalid org number');
      } else {
        toast.error(isNo ? 'Kunne ikke slå opp akkurat nå. Prøv igjen.' : 'Lookup failed. Try again.');
      }
    } finally {
      setLookingUp(false);
    }
  };

  const industries = [
    { value: 'bygg_anlegg', label: isNo ? 'Bygg og anlegg' : 'Construction' },
    { value: 'eiendom', label: isNo ? 'Eiendom' : 'Real estate' },
    { value: 'uteliv', label: isNo ? 'Uteliv og servering' : 'Hospitality' },
    { value: 'annet', label: isNo ? 'Annet / generell' : 'Other / general' },
  ];
  const sizes = [
    { value: 'liten', label: isNo ? 'Liten (1–20 ansatte)' : 'Small (1–20)' },
    { value: 'middels', label: isNo ? 'Middels (21–100 ansatte)' : 'Medium (21–100)' },
    { value: 'stor', label: isNo ? 'Stor (100+ ansatte)' : 'Large (100+)' },
  ];

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const token = localStorage.getItem('systemklart_token');
        const res = await axios.get(`${API}/organizations/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrg(res.data);
        setName(res.data?.name || '');
        setOrgnr(res.data?.orgnr || '');
        setNaceCode(res.data?.nace_code || '');
        setNaceDescription(res.data?.nace_description || '');
        setEmployees(res.data?.employees != null ? String(res.data.employees) : '');
        setIndustry(res.data?.industry || '');
        setSize(res.data?.size || '');
      } catch (err) {
        toast.error(isNo ? 'Kunne ikke laste bedriftsprofil' : 'Could not load company profile');
      } finally {
        setLoading(false);
      }
    };
    fetchOrg();
  }, [isNo]);

  const save = async () => {
    if (!name.trim()) {
      toast.error(isNo ? 'Bedriftsnavn er påkrevd' : 'Company name is required');
      return;
    }
    const orgnrClean = orgnr.replace(/\s+/g, '');
    if (orgnrClean && !/^\d{9}$/.test(orgnrClean)) {
      toast.error(isNo ? 'Organisasjonsnummer må være 9 siffer' : 'Organization number must be 9 digits');
      return;
    }
    const empClean = employees.toString().trim();
    let empValue = null;
    if (empClean) {
      const n = parseInt(empClean, 10);
      if (Number.isNaN(n) || n < 0 || n > 1000000) {
        toast.error(isNo ? 'Antall ansatte må være et heltall ≥ 0' : 'Employees must be a non-negative integer');
        return;
      }
      empValue = n;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.patch(
        `${API}/organizations/me`,
        {
          name: name.trim(),
          orgnr: orgnrClean || null,
          nace_code: naceCode || null,
          nace_description: naceDescription || null,
          employees: empValue,
          industry: industry || null,
          size: size || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isNo ? 'Bedriftsprofil oppdatert' : 'Company profile updated');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(isNo ? 'Kun administratorer kan endre bedriftsprofil' : 'Only admins can update company profile');
      } else {
        toast.error(isNo ? 'Kunne ikke lagre endringer' : 'Could not save changes');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse h-64 bg-slate-800/50 rounded-xl" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="company-profile-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{isNo ? 'Bedriftsprofil' : 'Company profile'}</h1>
            <p className="text-sm text-slate-400">
              {isNo
                ? 'Bedriftsnavn, bransje og størrelse. Brukes av AI ved dokumentgenerering.'
                : 'Company name, industry and size. Used by AI when generating documents.'}
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              {isNo ? 'Bedriftsnavn' : 'Company name'} <span className="text-rose-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
              placeholder={isNo ? 'F.eks. Kari AS' : 'e.g. Acme Inc.'}
              data-testid="company-name-input"
            />
          </div>

          {/* Orgnr */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              {isNo ? 'Organisasjonsnummer' : 'Organization number'}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={orgnr}
                onChange={(e) => setOrgnr(e.target.value)}
                inputMode="numeric"
                maxLength={11}
                className="flex-1 bg-slate-950/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 font-mono tracking-wider"
                placeholder={isNo ? '9 siffer (f.eks. 123456789)' : '9 digits'}
                data-testid="company-orgnr-input"
              />
              <button
                type="button"
                onClick={lookupOrgnr}
                disabled={lookingUp || !orgnr.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 text-sm font-medium hover:bg-cyan-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                data-testid="company-orgnr-lookup"
                title={isNo ? 'Slå opp i Brønnøysundregisteret' : 'Look up in Brreg'}
              >
                {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {isNo ? 'Slå opp' : 'Look up'}
              </button>
            </div>
            <p className="mt-1.5 text-xs text-slate-500">
              {isNo
                ? 'Skriv inn 9 siffer og klikk Slå opp — vi henter bedriftsnavn fra Brønnøysundregisteret.'
                : 'Enter 9 digits and click Look up — we fetch company name from Brreg.'}
            </p>
          </div>

          {/* NACE code (auto-filled from Brreg, editable) */}
          {(naceCode || naceDescription) && (
            <div className="rounded-lg border border-white/5 bg-slate-950/30 p-3" data-testid="nace-section">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                {isNo ? 'Næringskode (NACE)' : 'Industry code (NACE)'}
              </p>
              <p className="text-sm text-white">
                <span className="font-mono text-cyan-300">{naceCode}</span>
                {naceDescription && <span className="text-slate-300"> — {naceDescription}</span>}
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                {isNo ? 'Hentet fra Brønnøysundregisteret' : 'Fetched from Brreg'}
              </p>
            </div>
          )}

          {/* Employee count */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              {isNo ? 'Antall ansatte' : 'Number of employees'}
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={employees}
              onChange={(e) => setEmployees(e.target.value)}
              className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 tabular-nums"
              placeholder="0"
              data-testid="company-employees-input"
            />
            <p className="mt-1.5 text-xs text-slate-500">
              {isNo
                ? 'Hentes automatisk fra Brreg ved oppslag, men du kan justere manuelt. Brukes til skaleringskrav (verneombud ≥5, AMU ≥30 iht. arbeidsmiljøloven).'
                : 'Auto-filled from Brreg on lookup, but you can adjust manually. Used for scaling requirements (safety rep ≥5, AMU ≥30 per the Working Environment Act).'}
            </p>
          </div>

          {/* Industry */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              {isNo ? 'Bransje' : 'Industry'}
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500/50"
              data-testid="company-industry-select"
            >
              <option value="">{isNo ? 'Ikke valgt' : 'Not set'}</option>
              {industries.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-slate-500">
              {isNo
                ? 'Velg bransje som best beskriver bedriften. Du kan alltid endre senere.'
                : 'Select the industry that best describes your company. You can always change it later.'}
            </p>
          </div>

          {/* Size */}
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              {isNo ? 'Bedriftsstørrelse' : 'Company size'}
            </label>
            <select
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500/50"
              data-testid="company-size-select"
            >
              <option value="">{isNo ? 'Ikke valgt' : 'Not set'}</option>
              {sizes.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* Save */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
              data-testid="company-profile-cancel"
            >
              {isNo ? 'Avbryt' : 'Cancel'}
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 text-sm font-semibold rounded-lg transition-colors shadow-lg shadow-cyan-500/25"
              data-testid="company-profile-save"
            >
              <Save className="w-4 h-4" />
              {saving ? (isNo ? 'Lagrer…' : 'Saving…') : (isNo ? 'Lagre endringer' : 'Save changes')}
            </button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
